import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle,
  BookOpen,
  Check,
  GripVertical,
  ImageOff,
  Loader2,
  RotateCcw,
  Share2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';

const MEDIA_BASE = '/data/media';

const SUPPORTED_QUIZ_TYPES = ['memory-game', 'flashcards', 'image-pairing', 'image-sequencing'];

interface QuizWord {
  id: number;
  turkish: string;
  russian: string | null;
  transcription: string | null;
  imagePath: string | null;
  audioPath: string | null;
  sortOrder: number;
}

interface QuizData {
  id: number;
  title: string;
  slug: string;
  quizType: string;
  backgroundImagePath: string | null;
  /** Teacher-saved settings from the builder. Optional: older quizzes have none. */
  settings?: Record<string, unknown> | null;
  words: QuizWord[];
}

/** Read a boolean setting, defaulting when absent. */
function settingFlag(
  settings: QuizData['settings'],
  key: string,
  fallback: boolean,
): boolean {
  const value = settings?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** Read a non-empty string setting, defaulting when absent or blank. */
function settingText(
  settings: QuizData['settings'],
  key: string,
  fallback: string,
): string {
  const value = settings?.[key];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

const mediaUrl = (path: string) => `${MEDIA_BASE}/${path}`;

/** What a Russian-facing tile shows when the teacher has no translation yet. */
const russianText = (word: QuizWord) => (word.russian && word.russian.trim() ? word.russian : word.turkish);

const shuffleArray = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ------------------------------------------------------------------ memory game */

interface MemoryCard {
  id: number;
  wordId: number;
  text: string;
  type: 'tr' | 'ru';
  matched: boolean;
  flipped: boolean;
}

function buildMemoryCards(words: QuizWord[]): MemoryCard[] {
  return words.flatMap((w) => [
    { id: w.id * 2, wordId: w.id, text: w.turkish, type: 'tr' as const, matched: false, flipped: false },
    { id: w.id * 2 + 1, wordId: w.id, text: russianText(w), type: 'ru' as const, matched: false, flipped: false },
  ]);
}

/**
 * A pair resolves on the word it came from (`wordId`) and on the two sides being
 * different cards. Looking the word up by the text on the card used to bind two
 * Russian cards — or two words sharing a spelling — to the wrong entry, and a word
 * without a translation fell back to its Turkish text on both sides.
 */
function cardsMatch(a: MemoryCard, b: MemoryCard, words: QuizWord[]): boolean {
  if (a.wordId !== b.wordId || a.type === b.type) return false;
  const word = words.find((w) => w.id === a.wordId);
  if (!word) return false;
  const ru = russianText(word);
  return (
    (a.type === 'tr' && b.type === 'ru' && a.text === word.turkish && b.text === ru) ||
    (b.type === 'tr' && a.type === 'ru' && b.text === word.turkish && a.text === ru)
  );
}

/* ------------------------------------------------------------------ sequencing */

/** The answer key for image-sequencing is the teacher's own word order. */
function orderedWordIds(words: QuizWord[]): number[] {
  return [...words].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id).map((w) => w.id);
}

/* ------------------------------------------------------------------ components */

/** Never render a broken image icon: fall back to a neutral placeholder tile. */
function WordImage({ word, className = '' }: { word: QuizWord; className?: string }) {
  const [broken, setBroken] = useState(false);

  if (!word.imagePath || broken) {
    return (
      <div
        className={`flex items-center justify-center bg-brand-100 text-brand-300 ${className}`}
        role="img"
        aria-label="Изображение отсутствует"
      >
        <ImageOff size={24} />
      </div>
    );
  }

  return (
    <img
      src={mediaUrl(word.imagePath)}
      alt={word.turkish}
      onError={() => setBroken(true)}
      className={className}
    />
  );
}

/** Explicit playback control — never autoplays, reports a missing file. */
function AudioButton({ audioPath, className = '' }: { audioPath: string | null; className?: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => () => audioRef.current?.pause(), []);

  if (!audioPath) return null;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    audio.currentTime = 0;
    setFailed(false);
    void audio.play().catch(() => {
      setFailed(true);
      setPlaying(false);
    });
  };

  return (
    <span className="inline-flex flex-col items-center">
      <button
        type="button"
        onClick={toggle}
        aria-label="Произношение"
        title={failed ? 'Аудио недоступно' : playing ? 'Остановить' : 'Прослушать'}
        className={`flex items-center justify-center rounded-full shadow-sm transition-colors ${
          failed
            ? 'bg-gray-100 text-gray-400'
            : playing
            ? 'bg-brand-700 text-white'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        } ${className || 'h-10 w-10'}`}
      >
        {failed || playing ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>
      {failed && <span className="mt-1 text-[11px] text-red-500">Аудио недоступно</span>}
      <audio
        ref={audioRef}
        src={mediaUrl(audioPath)}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => {
          setFailed(true);
          setPlaying(false);
        }}
      />
    </span>
  );
}

function NoticeCard({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-center shadow-lg">
      <div className="flex justify-center text-brand-300">{icon}</div>
      <h2 className="mt-3 text-lg font-bold text-gray-900">{title}</h2>
      <p className="mt-2 text-sm text-gray-600">{hint}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ memory game view */

interface MemoryGameViewProps {
  words: QuizWord[];
  cards: MemoryCard[];
  flippedCount: number;
  matches: number;
  attempts: number;
  onCardClick: (index: number) => void;
}

function MemoryGameView({ words, cards, flippedCount, matches, attempts, onCardClick }: MemoryGameViewProps) {
  if (words.length === 0) {
    return (
      <NoticeCard
        icon={<BookOpen size={32} />}
        title="В тесте пока нет слов"
        hint="Добавьте слова в тест, чтобы сыграть. Обратитесь к учителю."
      />
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      <div className="mb-4 flex justify-between text-sm text-gray-600">
        <span>Попыток: {attempts}</span>
        <span>Пар: {matches} / {words.length}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {cards.map((card, idx) => (
          <button
            key={card.id}
            type="button"
            onClick={() => onCardClick(idx)}
            disabled={card.matched || card.flipped || flippedCount >= 2}
            className={`flex h-24 items-center justify-center rounded-xl text-center text-sm font-medium transition-all ${
              card.matched
                ? 'bg-green-100 text-green-700'
                : card.flipped
                ? 'bg-brand-100 text-brand-800'
                : 'bg-white text-gray-700 shadow-sm hover:bg-gray-50'
            }`}
          >
            {card.matched || card.flipped ? card.text : '?'}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ flashcards view */

interface FlashcardsViewProps {
  words: QuizWord[];
  index: number;
  flipped: boolean;
  onFlip: () => void;
  onGo: (index: number) => void;
  onFinish: () => void;
  settings?: Record<string, unknown> | null;
}

function FlashcardsView({ words, index, flipped, onFlip, onGo, onFinish, settings }: FlashcardsViewProps) {
  if (words.length === 0) {
    return (
      <NoticeCard
        icon={<BookOpen size={32} />}
        title="В тесте пока нет слов"
        hint="Добавьте слова в тест, чтобы начать заниматься. Обратитесь к учителю."
      />
    );
  }

  const current = words[index];
  const isLast = index >= words.length - 1;

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-4 text-center text-sm text-gray-600">
        Карточка {index + 1} из {words.length}
      </div>
      <button
        type="button"
        onClick={onFlip}
        className="flex h-64 w-full flex-col items-center justify-center rounded-2xl bg-white p-6 text-center shadow-lg transition-transform active:scale-95"
      >
        <WordImage word={current} className="mb-4 h-24 w-24 rounded-lg object-cover" />
        <span className="text-2xl font-bold text-gray-900">
          {flipped ? current.russian || '—' : current.turkish}
        </span>
        {flipped && !current.russian && <span className="mt-1 text-xs text-gray-400">Перевода нет</span>}
        <span className="mt-2 text-xs text-gray-400">Нажмите, чтобы перевернуть</span>
      </button>

      {current.audioPath && (
        <div className="mt-4 flex justify-center">
          <AudioButton audioPath={current.audioPath} />
        </div>
      )}

      <div className="mt-4 flex justify-center gap-3">
        <button
          type="button"
          onClick={() => onGo(index - 1)}
          disabled={index === 0}
          className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm disabled:opacity-50"
        >
          {settingText(settings, 'previous', 'Назад')}
        </button>
        {isLast ? (
          <button
            type="button"
            onClick={onFinish}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm"
          >
            Завершить
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onGo(index + 1)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm"
          >
            {settingText(settings, 'next', 'Далее')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ image pairing view */

type PairSide = 'image' | 'text';

interface PairSelection {
  side: PairSide;
  wordId: number;
}

interface ImagePairingViewProps {
  words: QuizWord[];
  textOrder: number[];
  matched: number[];
  selection: PairSelection | null;
  wrong: { imageWordId: number; textWordId: number } | null;
  attempts: number;
  onSelect: (side: PairSide, wordId: number) => void;
}

function ImagePairingView({
  words,
  textOrder,
  matched,
  selection,
  wrong,
  attempts,
  onSelect,
}: ImagePairingViewProps) {
  if (words.length === 0) {
    return (
      <NoticeCard
        icon={<BookOpen size={32} />}
        title="В тесте пока нет слов"
        hint="Добавьте слова с картинками в тест. Обратитесь к учителю."
      />
    );
  }

  const isMatched = (id: number) => matched.includes(id);

  const tileClass = (state: 'idle' | 'selected' | 'wrong' | 'matched') =>
    state === 'matched'
      ? 'border-green-400 bg-green-100 text-green-700'
      : state === 'wrong'
      ? 'border-red-500 bg-red-50 text-red-600'
      : state === 'selected'
      ? 'border-brand-500 bg-brand-50 text-brand-800'
      : 'border-transparent bg-white text-gray-700 shadow-sm hover:bg-gray-50';

  const stateOf = (side: PairSide, id: number) => {
    if (isMatched(id)) return 'matched' as const;
    if (side === 'image' && wrong?.imageWordId === id) return 'wrong' as const;
    if (side === 'text' && wrong?.textWordId === id) return 'wrong' as const;
    if (selection?.side === side && selection.wordId === id) return 'selected' as const;
    return 'idle' as const;
  };

  const byId = new Map(words.map((w) => [w.id, w]));

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex justify-between text-sm text-gray-600">
        <span>Попыток: {attempts}</span>
        <span>Пар: {matched.length} / {words.length}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          {words.map((word) => {
            const state = stateOf('image', word.id);
            return (
              <div key={word.id} className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => onSelect('image', word.id)}
                  disabled={state === 'matched'}
                  aria-label={word.turkish}
                  className={`relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border-2 transition-all ${
                    state === 'matched'
                      ? 'border-green-400 bg-green-50 opacity-60'
                      : state === 'wrong'
                      ? 'animate-pulse border-red-500 bg-red-50'
                      : state === 'selected'
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-transparent bg-white shadow-sm hover:bg-gray-50'
                  }`}
                >
                  <WordImage word={word} className="h-full w-full object-cover" />
                  {state === 'matched' && (
                    <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-white">
                      <Check size={14} />
                    </span>
                  )}
                </button>
                {state === 'matched' && (
                  <div className="flex items-center justify-center gap-1 text-xs font-medium text-green-700">
                    <span className="truncate">{word.turkish}</span>
                    <AudioButton audioPath={word.audioPath} className="h-7 w-7" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 sm:gap-4">
          {textOrder.map((id) => {
            const word = byId.get(id);
            if (!word) return null;
            const state = stateOf('text', word.id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => onSelect('text', word.id)}
                disabled={state === 'matched'}
                className={`flex w-full items-center justify-center rounded-xl border-2 px-2 py-3 text-center text-sm font-medium transition-all ${tileClass(state)}`}
              >
                <span className="flex flex-col items-center gap-1">
                  <span>{russianText(word)}</span>
                  {state === 'matched' && (
                    <span className="flex items-center gap-1 text-xs font-normal text-green-800">
                      <Check size={12} />
                      {word.turkish}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-gray-500">
        Выберите картинку и её значение — в любом порядке
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ image sequencing view */

interface ImageSequencingViewProps {
  words: QuizWord[];
  order: number[];
  answer: number[];
  checked: boolean;
  dragOverIndex: number | null;
  pickedIndex: number | null;
  onPick: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onDragStart: (index: number, event: DragEvent<HTMLDivElement>) => void;
  onDragOver: (index: number, event: DragEvent<HTMLDivElement>) => void;
  onDrop: (index: number, event: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onCheck: () => void;
  onShuffle: () => void;
  onFinish: () => void;
}

function ImageSequencingView({
  words,
  order,
  answer,
  checked,
  dragOverIndex,
  pickedIndex,
  onPick,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onCheck,
  onShuffle,
  onFinish,
}: ImageSequencingViewProps) {
  if (words.length === 0) {
    return (
      <NoticeCard
        icon={<BookOpen size={32} />}
        title="В тесте пока нет слов"
        hint="Добавьте слова с картинками в тест. Обратитесь к учителю."
      />
    );
  }

  const byId = new Map(words.map((w) => [w.id, w]));

  const moveByKeyboard = (index: number, event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPick(index);
      return;
    }
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault();
      onMove(index, index - 1);
    } else if (event.key === 'ArrowDown' && index < order.length - 1) {
      event.preventDefault();
      onMove(index, index + 1);
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <div className="mb-4 text-center text-sm text-gray-600">
        {checked ? 'Проверьте порядок и исправьте ошибки' : 'Расставьте карточки в правильном порядке'}
      </div>

      <ol className="flex flex-col gap-2">
        {order.map((id, index) => {
          const word = byId.get(id);
          if (!word) return null;
          const isOk = id === answer[index];
          const expected = byId.get(answer[index]);
          const isPicked = pickedIndex === index;
          const isDragOver = dragOverIndex === index && dragOverIndex !== pickedIndex;

          return (
            <li key={id} className="list-none">
              <div
                draggable
                role="button"
                tabIndex={0}
                aria-label={`Позиция ${index + 1}: ${word.turkish}`}
                onClick={() => onPick(index)}
                onKeyDown={(event) => moveByKeyboard(index, event)}
                onDragStart={(event) => onDragStart(index, event)}
                onDragOver={(event) => onDragOver(index, event)}
                onDrop={(event) => onDrop(index, event)}
                onDragEnd={onDragEnd}
                className={`flex cursor-grab items-center gap-3 rounded-xl border-2 bg-white p-2 shadow-sm transition-all active:cursor-grabbing ${
                  isDragOver ? 'border-brand-500' : isPicked ? 'border-brand-500 ring-2 ring-brand-200' : 'border-transparent'
                } ${checked && !isOk ? 'ring-2 ring-red-200' : ''}`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
                    checked && isOk ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-700'
                  }`}
                >
                  {index + 1}
                </span>
                <span className="h-16 w-16 shrink-0 overflow-hidden rounded-lg">
                  <WordImage word={word} className="h-full w-full object-cover" />
                </span>
                <span className="flex-1 text-sm font-medium text-gray-800">{word.turkish}</span>
                <GripVertical size={18} className="shrink-0 text-gray-300" />
                {checked &&
                  (isOk ? (
                    <Check size={20} className="shrink-0 text-green-600" />
                  ) : (
                    <X size={20} className="shrink-0 text-red-500" />
                  ))}
              </div>
              {checked && !isOk && expected && (
                <p className="ml-11 mt-1 text-xs text-gray-500">На этом месте: {expected.turkish}</p>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onCheck}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
        >
          {checked ? 'Проверить ещё раз' : 'Проверить'}
        </button>
        <button
          type="button"
          onClick={onShuffle}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RotateCcw size={16} />
          Перемешать
        </button>
        {checked && (
          <button
            type="button"
            onClick={onFinish}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm"
          >
            Завершить
          </button>
        )}
      </div>

      <p className="mt-3 text-center text-xs text-gray-500">
        Перетащите карточку мышкой или нажмите на две карточки, чтобы поменять их местами
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ page */

export default function QuizPlayerPage() {
  const { slug } = useParams<{ slug: string }>();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Memory game state
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [flippedIndices, setFlippedIndices] = useState<number[]>([]);
  const [matches, setMatches] = useState(0);
  const [attempts, setAttempts] = useState(0);

  // Flashcards state
  const [fcIndex, setFcIndex] = useState(0);
  const [fcFlipped, setFcFlipped] = useState(false);

  // Image pairing state
  const [pairTextOrder, setPairTextOrder] = useState<number[]>([]);
  const [pairMatched, setPairMatched] = useState<number[]>([]);
  const [pairSelection, setPairSelection] = useState<PairSelection | null>(null);
  const [pairWrong, setPairWrong] = useState<{ imageWordId: number; textWordId: number } | null>(null);
  const [pairAttempts, setPairAttempts] = useState(0);

  // Image sequencing state
  const [seqOrder, setSeqOrder] = useState<number[]>([]);
  const [seqChecked, setSeqChecked] = useState(false);
  const [seqDragOverIndex, setSeqDragOverIndex] = useState<number | null>(null);
  const [seqPickedIndex, setSeqPickedIndex] = useState<number | null>(null);
  const seqDragFrom = useRef<number | null>(null);

  // Result state
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);

  // Every deferred reveal/reset goes through here so a restart cannot fire a stale timer.
  const timers = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await fetch(`/api/public/quizzes/${slug}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Failed to load quiz');
        }
        const data = await res.json();
        setQuiz(data.data);
      } catch (err: any) {
        setError(err.message || 'Ошибка загрузки теста');
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [slug]);

  const words = useMemo(() => quiz?.words ?? [], [quiz]);
  const total = words.length;
  const seqAnswer = useMemo(() => orderedWordIds(words), [words]);
  const seqCorrect = seqChecked
    ? seqOrder.reduce((acc, id, index) => acc + (id === seqAnswer[index] ? 1 : 0), 0)
    : 0;
  const seqPerfect = seqChecked && total > 0 && seqOrder.every((id, index) => id === seqAnswer[index]);
  const isSupported = !!quiz && SUPPORTED_QUIZ_TYPES.includes(quiz.quizType);
  // Builder-authored settings. Defaults keep older quizzes behaving exactly as before.
  const allowRetry = settingFlag(quiz?.settings, 'allowRetry', true);

  useEffect(() => {
    if (!quiz) return;
    if (quiz.quizType === 'memory-game') {
      setCards(shuffleArray(buildMemoryCards(quiz.words)));
    } else if (quiz.quizType === 'image-pairing') {
      setPairTextOrder(shuffleArray(quiz.words.map((w) => w.id)));
    } else if (quiz.quizType === 'image-sequencing') {
      setSeqOrder(shuffleArray(orderedWordIds(quiz.words)));
    }
  }, [quiz]);

  // A perfect run still shows its green feedback before the result card.
  useEffect(() => {
    if (!seqPerfect) return;
    const id = window.setTimeout(() => setShowResult(true), 900);
    return () => window.clearTimeout(id);
  }, [seqPerfect]);

  const handleCardClick = (index: number) => {
    const card = cards[index];
    if (card.matched || card.flipped || flippedIndices.length >= 2) return;

    const nextCards = cards.map((c, i) => (i === index ? { ...c, flipped: true } : c));
    setCards(nextCards);

    const nextFlipped = [...flippedIndices, index];
    setFlippedIndices(nextFlipped);
    if (nextFlipped.length < 2) return;

    setAttempts((a) => a + 1);
    const [i1, i2] = nextFlipped;
    const c1 = nextCards[i1];
    const c2 = nextCards[i2];

    if (cardsMatch(c1, c2, words)) {
      // Only one pair can resolve at a time (a third flip is blocked until this
      // timer clears the flipped cards), so the count at click time is the count here.
      later(() => {
        setCards((prev) => prev.map((c, i) => (i === i1 || i === i2 ? { ...c, matched: true } : c)));
        setMatches(matches + 1);
        setFlippedIndices([]);
        if (matches + 1 === total) later(() => setShowResult(true), 400);
      }, 600);
    } else {
      later(() => {
        setCards((prev) => prev.map((c, i) => (i === i1 || i === i2 ? { ...c, flipped: false } : c)));
        setFlippedIndices([]);
      }, 1000);
    }
  };

  const handlePairSelect = (side: PairSide, wordId: number) => {
    if (pairMatched.includes(wordId)) return;

    if (!pairSelection) {
      setPairSelection({ side, wordId });
      return;
    }
    // Re-picking the same side moves the selection; tapping it again clears it.
    if (pairSelection.side === side) {
      setPairSelection(pairSelection.wordId === wordId ? null : { side, wordId });
      return;
    }
    if (pairSelection.wordId === wordId) {
      const nextMatched = [...pairMatched, wordId];
      setPairMatched(nextMatched);
      setPairSelection(null);
      if (nextMatched.length === total) later(() => setShowResult(true), 600);
      return;
    }

    setPairAttempts((a) => a + 1);
    setPairWrong({
      imageWordId: side === 'image' ? wordId : pairSelection.wordId,
      textWordId: side === 'text' ? wordId : pairSelection.wordId,
    });
    setPairSelection(null);
    later(() => setPairWrong(null), 700);
  };

  const handleSeqPick = (index: number) => {
    if (seqPickedIndex === null) {
      setSeqPickedIndex(index);
      return;
    }
    if (seqPickedIndex === index) {
      setSeqPickedIndex(null);
      return;
    }
    const from = seqPickedIndex;
    setSeqOrder((prev) => {
      const next = [...prev];
      const moved = next[from];
      next[from] = next[index];
      next[index] = moved;
      return next;
    });
    setSeqPickedIndex(null);
    setSeqChecked(false);
  };

  const moveSeqItem = useCallback((from: number, to: number) => {
    setSeqOrder((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const moved = next[from];
      next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setSeqChecked(false);
    setSeqPickedIndex(null);
  }, []);

  const handleSeqDragStart = (index: number, event: DragEvent<HTMLDivElement>) => {
    seqDragFrom.current = index;
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts a drag only when some payload is attached.
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleSeqDragOver = (index: number, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (seqDragFrom.current !== null && seqDragFrom.current !== index) {
      setSeqDragOverIndex(index);
    }
  };

  const handleSeqDrop = (index: number, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const from = seqDragFrom.current;
    seqDragFrom.current = null;
    setSeqDragOverIndex(null);
    if (from === null) return;
    moveSeqItem(from, index);
  };

  const handleSeqDragEnd = () => {
    seqDragFrom.current = null;
    setSeqDragOverIndex(null);
  };

  const handleSeqCheck = () => setSeqChecked(true);
  const handleSeqShuffle = () => {
    setSeqOrder(shuffleArray(seqAnswer));
    setSeqChecked(false);
    setSeqPickedIndex(null);
    setSeqDragOverIndex(null);
    seqDragFrom.current = null;
  };

  const summary = !quiz
    ? ''
    : quiz.quizType === 'image-pairing'
    ? `${pairMatched.length} пар из ${total} за ${pairAttempts} попыток`
    : quiz.quizType === 'image-sequencing'
    ? `${seqCorrect} из ${total} позиций в правильном порядке`
    : quiz.quizType === 'flashcards'
    ? `изучено ${total} слов`
    : `${matches} пар из ${total} за ${attempts} попыток`;

  const handleShare = useCallback(async () => {
    const text = `Я прошел тест "${quiz?.title}" с результатом ${summary}!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: quiz?.title || 'Lexica', text, url: window.location.href });
      } catch { /* ignore */ }
    } else {
      await navigator.clipboard.writeText(text + ' ' + window.location.href);
      setCopied(true);
    }
  }, [quiz, summary]);

  const resetQuiz = () => {
    clearTimers();
    setShowResult(false);
    setMatches(0);
    setAttempts(0);
    setFlippedIndices([]);
    setFcIndex(0);
    setFcFlipped(false);
    setPairMatched([]);
    setPairSelection(null);
    setPairWrong(null);
    setPairAttempts(0);
    setSeqChecked(false);
    setSeqPickedIndex(null);
    setSeqDragOverIndex(null);
    seqDragFrom.current = null;

    if (quiz?.quizType === 'memory-game') {
      setCards(shuffleArray(buildMemoryCards(quiz.words)));
    } else if (quiz?.quizType === 'image-pairing') {
      setPairTextOrder(shuffleArray(quiz.words.map((w) => w.id)));
    } else if (quiz?.quizType === 'image-sequencing') {
      setSeqOrder(shuffleArray(orderedWordIds(quiz.words)));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-50">
        <Loader2 className="h-10 w-10 animate-spin text-brand-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-brand-50 p-4">
        <BookOpen size={48} className="text-brand-300" />
        <h1 className="mt-4 text-xl font-bold text-gray-800">{error}</h1>
        <p className="mt-2 text-gray-500">Вернуться позже или обратитесь к учителю.</p>
      </div>
    );
  }

  if (!quiz) return null;

  const bgStyle = quiz.backgroundImagePath
    ? { backgroundImage: `url(/data/media/${quiz.backgroundImagePath})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {};

  return (
    <div className="flex min-h-screen flex-col bg-brand-50" style={bgStyle}>
      <div className="flex items-center justify-between bg-white/90 p-4 shadow-sm backdrop-blur">
        <div className="flex items-center gap-2 text-brand-700">
          <BookOpen size={20} />
          <span className="font-bold">Lexica</span>
        </div>
        <h1 className="text-sm font-medium text-gray-700">{quiz.title}</h1>
      </div>

      <div className="flex-1 p-4">
        {!isSupported && (
          <NoticeCard
            icon={<AlertCircle size={32} />}
            title="Неподдерживаемый тип теста"
            hint="Этот формат пока не доступен для прохождения. Обратитесь к учителю."
          />
        )}

        {isSupported && !showResult && quiz.quizType === 'memory-game' && (
          <MemoryGameView
            words={words}
            cards={cards}
            flippedCount={flippedIndices.length}
            matches={matches}
            attempts={attempts}
            onCardClick={handleCardClick}
          />
        )}

        {isSupported && !showResult && quiz.quizType === 'flashcards' && (
          // settings drives the editable button labels
          <FlashcardsView
            words={words}
            index={fcIndex}
            flipped={fcFlipped}
            onFlip={() => setFcFlipped(!fcFlipped)}
            onGo={(i) => {
              setFcFlipped(false);
              setFcIndex(Math.max(0, Math.min(words.length - 1, i)));
            }}
            onFinish={() => setShowResult(true)}
            settings={quiz.settings}
          />
        )}

        {isSupported && !showResult && quiz.quizType === 'image-pairing' && (
          <ImagePairingView
            words={words}
            textOrder={pairTextOrder}
            matched={pairMatched}
            selection={pairSelection}
            wrong={pairWrong}
            attempts={pairAttempts}
            onSelect={handlePairSelect}
          />
        )}

        {isSupported && !showResult && quiz.quizType === 'image-sequencing' && (
          <ImageSequencingView
            words={words}
            order={seqOrder}
            answer={seqAnswer}
            checked={seqChecked}
            dragOverIndex={seqDragOverIndex}
            pickedIndex={seqPickedIndex}
            onPick={handleSeqPick}
            onMove={moveSeqItem}
            onDragStart={handleSeqDragStart}
            onDragOver={handleSeqDragOver}
            onDrop={handleSeqDrop}
            onDragEnd={handleSeqDragEnd}
            onCheck={handleSeqCheck}
            onShuffle={handleSeqShuffle}
            onFinish={() => setShowResult(true)}
          />
        )}

        {isSupported && showResult && (
          <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-center shadow-lg">
            {copied && (
              <p className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                Результат скопирован в буфер обмена
              </p>
            )}
            <h2 className="text-2xl font-bold text-gray-900">Результат</h2>
            {quiz.quizType === 'memory-game' && (
              <p className="mt-2 text-gray-600">
                Вы нашли {matches} пар из {total} за {attempts} попыток
              </p>
            )}
            {quiz.quizType === 'flashcards' && (
              <p className="mt-2 text-gray-600">Вы изучили {total} слов!</p>
            )}
            {quiz.quizType === 'image-pairing' && (
              <p className="mt-2 text-gray-600">
                Вы составили {pairMatched.length} пар из {total} за {pairAttempts} попыток
              </p>
            )}
            {quiz.quizType === 'image-sequencing' && (
              <p className="mt-2 text-gray-600">
                Правильно {seqCorrect} из {total} позиций
              </p>
            )}
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <Share2 size={16} />
                Поделиться
              </button>
              {/* Teacher can disable retrying from the builder. */}
              {allowRetry && (
                <button
                  onClick={resetQuiz}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <RotateCcw size={16} />
                  Ещё раз
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}